import { httpsCallable } from 'firebase/functions';
import { functions } from '@lib/firebase';

import { ApiError, ValidationError } from '@/shared/api/errors/ApiError';
import ServiceBase from '@/shared/api/base/ServiceBase';

// Type definitions
interface StudentVerificationData {
    studentName: string;
    studentDOB: string;
}

interface EmailSendResult {
    success: boolean;
    message: string;
    tokenCreatedAt?: string;
}

interface ConsentVerificationResult {
    success: boolean;
    message: string;
}

interface GetStudentResponse {
    data: {
        studentName: string;
        studentDOB: string;
    };
}

interface SendEmailResponse {
    data: {
        message: string;
        tokenCreatedAt: string;
    };
}

interface VerifyConsentResponse {
    data: {
        message: string;
    };
}

class ParentalConsentService extends ServiceBase {
    constructor() {
        super('ParentalConsentService');
    }

    async getStudentForVerification(studentId: string, token: string): Promise<StudentVerificationData> {
        if (!studentId || !token) {
            throw new ValidationError('Student ID and token are required');
        }

        try {
            const getStudentFunction = httpsCallable<{ studentId: string; token: string }, GetStudentResponse['data']>(functions, 'getStudentForVerification');
            const result = await getStudentFunction({
                studentId,
                token,
            });

            this.log(`Fetched student data for verification: ${studentId}`);
            return {
                studentName: result.data.studentName,
                studentDOB: result.data.studentDOB,
            };
        } catch (error) {
            this.logError(error, { method: 'getStudentForVerification', studentId });

            const errorMessage = error instanceof Error ? error.message : String(error);

            if (errorMessage.includes('INVALID_TOKEN')) {
                throw new ApiError(
                    'INVALID_TOKEN',
                    'Invalid or expired verification link',
                    error
                );
            }
            if (errorMessage.includes('ALREADY_VERIFIED')) {
                throw new ApiError(
                    'ALREADY_VERIFIED',
                    'Consent has already been verified',
                    error
                );
            }
            if (errorMessage.includes('TOKEN_EXPIRED')) {
                throw new ApiError(
                    'TOKEN_EXPIRED',
                    'Verification link has expired. Please ask the student to request a new email.',
                    error
                );
            }
            if (errorMessage.includes('USER_NOT_FOUND')) {
                throw new ApiError(
                    'USER_NOT_FOUND',
                    'Student record not found',
                    error
                );
            }

            throw new ApiError(
                'FETCH_STUDENT_FAILED',
                'Failed to fetch student data. Please try again.',
                error
            );
        }
    }

    async sendParentConsentEmail(studentId: string, parentEmail: string): Promise<EmailSendResult> {
        if (!studentId) {
            throw new ValidationError('Student ID is required');
        }
        if (!parentEmail || !parentEmail.includes('@')) {
            throw new ValidationError('Valid parent email is required');
        }

        try {
            const sendEmailFunction = httpsCallable<{ studentId: string; parentEmail: string }, SendEmailResponse['data']>(functions, 'sendParentConsentEmail');
            const result = await sendEmailFunction({
                studentId,
                parentEmail,
            });

            this.log(`Sent parent consent email for student ${studentId} to ${parentEmail}`);
            return {
                success: true,
                message: result.data.message,
                tokenCreatedAt: result.data.tokenCreatedAt,
            };
        } catch (error) {
            this.logError(error, { method: 'sendParentConsentEmail', studentId, parentEmail });

            const errorMessage = error instanceof Error ? error.message : String(error);

            if (errorMessage.includes('PERMISSION_DENIED')) {
                throw new ApiError(
                    'PERMISSION_DENIED',
                    'You can only send consent requests for your own account',
                    error
                );
            }
            if (errorMessage.includes('USER_NOT_FOUND')) {
                throw new ApiError(
                    'USER_NOT_FOUND',
                    'Student record not found',
                    error
                );
            }

            throw new ApiError(
                'SEND_EMAIL_FAILED',
                'Failed to send verification email. Please try again.',
                error
            );
        }
    }

    async verifyParentConsent(
        studentId: string,
        token: string,
        parentName: string,
        signature: string,
        phoneNumber: string = ''
    ): Promise<ConsentVerificationResult> {
        if (!studentId || !token || !parentName || !signature) {
            throw new ValidationError('Student ID, token, name, and signature are required');
        }

        if (signature !== parentName) {
            throw new ValidationError('Signature must match the parent name exactly');
        }

        try {
            const verifyParentConsentFunction = httpsCallable<
                { studentId: string; token: string; parentName: string; signature: string; phoneNumber: string; userAgent: string },
                VerifyConsentResponse['data']
            >(functions, 'verifyParentConsent');
            const result = await verifyParentConsentFunction({
                studentId,
                token,
                parentName,
                signature,
                phoneNumber,
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Server',
            });

            this.log(`Verified parent consent for student ${studentId}`);
            return {
                success: true,
                message: result.data.message,
            };
        } catch (error) {
            this.logError(error, { method: 'verifyParentConsent', studentId });

            const errorMessage = error instanceof Error ? error.message : String(error);

            if (errorMessage.includes('INVALID_TOKEN')) {
                throw new ApiError(
                    'INVALID_TOKEN',
                    'Invalid or expired verification token',
                    error
                );
            }
            if (errorMessage.includes('ALREADY_VERIFIED')) {
                throw new ApiError(
                    'ALREADY_VERIFIED',
                    'Consent has already been verified',
                    error
                );
            }
            if (errorMessage.includes('TOKEN_EXPIRED')) {
                throw new ApiError(
                    'TOKEN_EXPIRED',
                    'Verification link has expired. Please ask the student to request a new email.',
                    error
                );
            }
            if (errorMessage.includes('VALIDATION_ERROR')) {
                throw new ApiError(
                    'VALIDATION_ERROR',
                    'Signature must match your full name exactly',
                    error
                );
            }

            throw new ApiError(
                'VERIFICATION_FAILED',
                'Failed to verify parent consent. Please try again.',
                error
            );
        }
    }
}

const parentalConsentService = new ParentalConsentService();
export default parentalConsentService;

// Named exports for backward compatibility
export const getStudentForVerification = (id: string, token: string) => parentalConsentService.getStudentForVerification(id, token);
export const sendParentConsentEmail = (id: string, email: string) => parentalConsentService.sendParentConsentEmail(id, email);
export const verifyParentConsent = (id: string, token: string, name: string, sig: string, phone?: string) => parentalConsentService.verifyParentConsent(id, token, name, sig, phone);
